import { useEffect, useMemo, useState } from 'react';
import { Download, ScrollText, Award } from 'lucide-react';
import api from '../api';

// Read the production-safe API host the same way AdminDashboard does so
// download URLs work both in the preview tunnel and on the deployed domain.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

// Admin → Doctoral Registry
// Chronological ledger of every doctoral conferral the institute has issued
// under the Vital Health Association credential.
const DoctoralRegistryPanel = () => {
  const [data, setData] = useState({ total: 0, per_program: {}, entries: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/admin/doctoral-registry');
        if (active) setData(res.data || { total: 0, per_program: {}, entries: [] });
      } catch (_e) {
        if (active) setData({ total: 0, per_program: {}, entries: [] });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const entries = data.entries || [];
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return entries;
    return entries.filter((e) =>
      (e.user_name || '').toLowerCase().includes(n) ||
      (e.conferral_number || '').toLowerCase().includes(n) ||
      (e.program_title || '').toLowerCase().includes(n) ||
      (e.verification_code || '').toLowerCase().includes(n)
    );
  }, [entries, q]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch (_e) {
      return iso;
    }
  };

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
  const downloadUrl = (e) =>
    `${API_HOST}/api/online-courses/${e.course_id}/certificate/${e.user_id}?_=${Date.now()}`;

  const handleDownload = async (e) => {
    // Use fetch so we can attach the bearer token, then open as a blob URL.
    try {
      const res = await fetch(downloadUrl(e), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        alert('Could not load the diploma. Please try again.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (_err) {
      alert('Could not load the diploma. Please try again.');
    }
  };

  return (
    <div data-testid="doctoral-registry-panel">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <ScrollText size={22} color="#5c1a1b" />
          Doctoral Registry
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9375rem', marginTop: 6, marginBottom: 0 }}>
          Official ledger of every doctoral diploma conferred under the
          Vital Health Association credential. Each conferral number is
          minted at the moment the diploma is issued and is permanent.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div
          style={{
            background: 'linear-gradient(135deg, #5c1a1b 0%, #7c2d12 100%)',
            color: '#fbf6e8',
            padding: '14px 18px',
            borderRadius: 10,
            minWidth: 180,
          }}
          data-testid="doctoral-registry-total"
        >
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.85 }}>
            Total Conferrals
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'Georgia, serif', marginTop: 4 }}>
            {data.total}
          </div>
        </div>
        {Object.entries(data.per_program || {}).map(([cid, count]) => (
          <div
            key={cid}
            style={{
              background: '#fef3c7',
              color: '#7c2d12',
              padding: '14px 18px',
              borderRadius: 10,
              minWidth: 160,
              borderLeft: '4px solid #d4af37',
            }}
          >
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.75 }}>
              {cid.replace(/-/g, ' ')}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Georgia, serif', marginTop: 4 }}>
              {count}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        <input
          type="text"
          placeholder="Search by candidate, conferral number, program, or verification code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="form-input"
          style={{ width: '100%', maxWidth: 520 }}
          data-testid="doctoral-registry-search"
        />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading registry…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state" style={{ padding: 48 }}>
          <div className="empty-state-icon"><Award size={42} color="#5c1a1b" /></div>
          <h3>No doctoral conferrals yet</h3>
          <p style={{ color: '#64748b' }}>
            Issue a doctoral diploma from the Student Transcripts tab to begin the registry.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table" data-testid="doctoral-registry-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Conferral No.</th>
                  <th>Candidate</th>
                  <th>Program</th>
                  <th>Conferral Date</th>
                  <th>Verification Code</th>
                  <th style={{ textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, idx) => (
                  <tr key={e.conferral_number || idx} data-testid={`doctoral-row-${e.conferral_number}`}>
                    <td style={{ color: '#64748b' }}>{idx + 1}</td>
                    <td style={{
                      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                      fontWeight: 700,
                      color: '#5c1a1b',
                    }}>
                      {e.conferral_number}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {e.user_name}
                      {e.apprentice_id && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>
                          Apprentice ID: {e.apprentice_id}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>{e.program_title}</td>
                    <td>{fmtDate(e.certificate_generated_at || e.completed_at)}</td>
                    <td style={{
                      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                      fontSize: '0.78rem',
                      color: '#475569',
                    }}>
                      {e.verification_code}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleDownload(e)}
                        className="btn btn-sm"
                        style={{
                          background: '#5c1a1b',
                          color: 'white',
                          padding: '6px 12px',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                        data-testid={`download-diploma-${e.conferral_number}`}
                      >
                        <Download size={14} />
                        Diploma
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctoralRegistryPanel;
